import {
    normalizeWidgetTheme,
    APIURL,
    cancelSelectEvent,
    changeSymbolForWidget,
    getCSSStyles,
    getSymbolDropdown,
    getTraderSMCardHTML,
    getWidgetDataURI,
    apiGet,
    toggleWidgetTheme,
    getEChartsTooltipTheme,
    getHeaderPinStateClass,
    getHeaderPinButtonHTML,
    setupHeaderPin
} from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

type PeriodType = 'annual' | 'quarterly';
type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';

interface RevenueSegmentItem {
    region: string;
    revenue: number;
    revenue_millions: number;
    revenue_billions: number;
    percentage: number;
    display_value: string;
    percentage_display: string;
}

interface RevenueGeographicalData {
    symbol: string;
    country: string;
    year: number;
    period_type: PeriodType;
    quarter: Quarter;
    total_revenue: number;
    total_revenue_billions: number;
    region_count: number;
    bar_chart_data: RevenueSegmentItem[];
}

interface RevenueGeographicalSegmentsConfig extends WidgetConfig {
    year?: number;
    periodType?: PeriodType;
    quarter?: Quarter;
}

const SEGMENT_COLORS = ['#7a00ff', '#4c00d6', '#33D17A', '#FFB020', '#FF6B6B', '#00B4D8', '#9B59B6', '#E67E22'];

export class TraderverseRevenueGeographicalSegments {
    private container: HTMLElement;
    private config: RevenueGeographicalSegmentsConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: RevenueGeographicalData | null = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
    private filters = {
        year: new Date().getFullYear(),
        periodType: 'annual' as PeriodType,
        quarter: 'Q1' as Quarter
    };

    constructor(config: RevenueGeographicalSegmentsConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<RevenueGeographicalSegmentsConfig> {
        const symbols = this.fetchSymbols();
        return {
            headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true,
            year: new Date().getFullYear(),
            periodType: 'annual',
            quarter: 'Q1'
        };
    }

    private applyConfigFilters(): void {
        if (this.config.year) this.filters.year = this.config.year;
        if (this.config.periodType) this.filters.periodType = this.config.periodType;
        if (this.config.quarter) this.filters.quarter = this.config.quarter;
    }

    private getYearOptions(): number[] {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 12 }, (_, i) => currentYear - i);
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private filtersIconSvg(): string {
        return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
    }

    private renderYearOptions(): string {
        return this.getYearOptions()
            .map(year => `<option value="${year}"${year === this.filters.year ? ' selected' : ''}>${year}</option>`)
            .join('');
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.attachStyles();
        this.setupEventListeners();
        await this.fetchData(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
  <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Revenue Geographical Segments</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()}</button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow" data-testing-id="options-row">
                ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                    <div class="trader-sm-card" id="selected-symbol">
                        ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
                    </div>
                    <span id="symbol-dropdown-placeholder"></span>
                </div>`}
                <span></span>
                <div class="filtersBox sideBar hidden">
                  <div class="item">
                    <label>Year</label>
                    <select class="year-select">${this.renderYearOptions()}</select>
                  </div>
                  <div class="item">
                    <label>Period Type</label>
                    <select class="period-type-select">
                      <option value="annual"${this.filters.periodType === 'annual' ? ' selected' : ''}>Annual</option>
                      <option value="quarterly"${this.filters.periodType === 'quarterly' ? ' selected' : ''}>Quarterly</option>
                    </select>
                  </div>
                  <div class="item">
                    <label>Quarter</label>
                    <select class="quarter-select">
                      <option value="Q1"${this.filters.quarter === 'Q1' ? ' selected' : ''}>Q1</option>
                      <option value="Q2"${this.filters.quarter === 'Q2' ? ' selected' : ''}>Q2</option>
                      <option value="Q3"${this.filters.quarter === 'Q3' ? ' selected' : ''}>Q3</option>
                      <option value="Q4"${this.filters.quarter === 'Q4' ? ' selected' : ''}>Q4</option>
                    </select>
                  </div>
                </div>
            </div>
            <div class="chartDv" data-testing-id="chart-container">
                <div class="chart" id="revenueGeographicalSegmentsChart" data-testing-id="revenue-geographical-segments-chart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) placeholder.replaceWith(dropdown);
        }

        const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filterBtn && filtersBox) {
            filterBtn.addEventListener('click', (ev: MouseEvent) => {
                ev.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }
    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private setupEventListeners(): void {
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        if (filtersBox) {
            filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
            this.outsideClickHandler = (ev: MouseEvent) => {
                const anyEv: any = ev;
                const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
                const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
                if (!clickedInside && !filtersBox.classList.contains('hidden')) {
                    filtersBox.classList.add('hidden');
                }
            };
            document.addEventListener('click', this.outsideClickHandler);
        }

        const yearSelect = this.shadowRoot.querySelector('.year-select') as HTMLSelectElement;
        const periodTypeSelect = this.shadowRoot.querySelector('.period-type-select') as HTMLSelectElement;
        const quarterSelect = this.shadowRoot.querySelector('.quarter-select') as HTMLSelectElement;

        const applyFiltersAndFetch = () => {
            this.filters.year = Number(yearSelect?.value || this.filters.year);
            this.filters.periodType = (periodTypeSelect?.value || this.filters.periodType) as PeriodType;
            this.filters.quarter = (quarterSelect?.value || this.filters.quarter) as Quarter;
            this.config.year = this.filters.year;
            this.config.periodType = this.filters.periodType;
            this.config.quarter = this.filters.quarter;
            this.fetchData(this.config.symbol!);
        };

        yearSelect?.addEventListener('change', applyFiltersAndFetch);
        periodTypeSelect?.addEventListener('change', applyFiltersAndFetch);
        quarterSelect?.addEventListener('change', applyFiltersAndFetch);

        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        dropdown?.addEventListener('change', e => {
            const value = (e.target as HTMLSelectElement).value;
            this.changeSymbol(value);
        });
        if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async fetchData(symbol: string): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');

        try {
            if ((this.config as any).data) {
                this.currentData = (this.config as any).data;
                this.updateDisplay();
                this.emit('dataUpdate', this.currentData);
                return;
            }

            const response = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/symbol-specific/revenue-geographical-segments`,
                query: {
                    symbol,
                    year: this.filters.year,
                    periodType: this.filters.periodType,
                    quarter: this.filters.quarter
                },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error(`API error: ${response.status}`);
            const json = await response.json();
            if (!json?.isSuccess) throw new Error(json?.message || 'Failed to fetch revenue geographical segments');

            this.currentData = json.data as RevenueGeographicalData;
            this.updateDisplay();
            this.emit('dataUpdate', {
                symbol,
                year: this.filters.year,
                periodType: this.filters.periodType,
                quarter: this.filters.quarter,
                data: this.currentData
            });
        } catch (err) {
            console.error('Error fetching chart data:', err);
            this.currentData = null;
            this.updateDisplay();
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
        if (this.currentData?.bar_chart_data?.length) {
            chartDv?.setAttribute('data-testing-id', 'revenue-geographical-segments-chartdv');
        } else {
            chartDv?.removeAttribute('data-testing-id');
        }

        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private shortenRegionName(region: string): string {
        return region.replace(/\s+Segment$/i, '');
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#revenueGeographicalSegmentsChart') as HTMLElement;
        if (!container) return;

        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
            this.currentChart = null;
        }

        const segments = this.currentData?.bar_chart_data || [];
        if (!segments.length) {
            container.innerHTML = '<div class="noData"><p>No revenue geographical segment data available.</p></div>';
            return;
        }

        container.innerHTML = '';

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');

        const labels = segments.map(item => this.shortenRegionName(item.region));
        const values = segments.map(item => item.revenue_billions);

        const chart = echarts.init(container, null, {
            backgroundColor: getComputedStyle(widgetBox).getPropertyValue('--bg-primary').trim()
        });
        this.currentChart = chart;

        const option = {
            backgroundColor: 'transparent',
            animation: true,
            grid: { left: 0, right: 0, top: 24, bottom: 0, containLabel: true },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const idx = params?.[0]?.dataIndex ?? 0;
                    const item = segments[idx];
                    if (!item) return '';
                    return `<b>${item.region}</b><br/>Revenue: ${item.display_value}<br/>Share: ${item.percentage_display}`;
                }
            },
            xAxis: {
                type: 'category',
                data: labels,
                axisLine: { lineStyle: { color: axisLineColor } },
                axisTick: { show: false },
                axisLabel: {
                    color: textColor,
                    fontSize,
                    interval: 0,
                    
                    overflow: 'break'
                }
            },
            yAxis: {
                type: 'value',
                axisLine: { show: false },
                splitLine: { show: false },
                axisLabel: {
                    color: textColor,
                    fontSize,
                    formatter: (value: number) => `$${value}B`
                }
            },
            series: [{
                name: 'Revenue',
                type: 'bar',
                data: values.map((value, index) => ({
                    value,
                    itemStyle: {
                        borderRadius: [6, 6, 0, 0],
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: SEGMENT_COLORS[index % SEGMENT_COLORS.length] },
                            { offset: 1, color: '#4c00d6' }
                        ])
                    }
                })),
                barWidth: '48%',
                label: {
                    show: true,
                    position: 'top',
                    color: textColor,
                    fontSize,
                    formatter: (params: any) => {
                        const item = segments[params.dataIndex];
                        return item ? `${item.display_value} (${item.percentage_display})` : '';
                    }
                }
            }]
        };

        chart.setOption(option);
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (stockId: any) => this.fetchData(stockId),
            (type: any, data: any) => this.emit(type, data)
        );
    }

    public updateWithResponse(resp: any): void {
        if (!resp) return;
        this.currentData = resp;
        this.updateDisplay();
        this.emit('dataUpdate', resp);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;
        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite';
        this.updateDisplay();
    }

    public destroy(): void {
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseRevenueGeographicalSegments: typeof TraderverseRevenueGeographicalSegments; } }
if (typeof window !== 'undefined') { window.TraderverseRevenueGeographicalSegments = TraderverseRevenueGeographicalSegments; }
